/* eslint-disable jsx-a11y/no-autofocus */
import React, { forwardRef } from 'react';
import proptypes from 'prop-types';
import classNames from 'classnames';
import { LoadingButton, IconButton } from '@one-thd/sui-atomic-components';
import { Search, Close } from '@one-thd/sui-icons';

const { shape, bool, func, string } = proptypes;

export const SEARCH_CLEAR_BUTTON_ID = 'typeahead-clear-icon-button';

const SearchField = forwardRef(({
  onSearchIconClick,
  showSearchIcon,
  onClearIconClick,
  isLoading,
  formProps,
  inputProps,
  customClasses,
  transparent
}, ref) => {

  const controllerClasses = [
    'sui-inline-flex sui-flex-row sui-relative',
    'sui-min-w-0 sui-p-0 sui-m-0',
    'sui-align-top sui-w-full'
  ].join(' ');

  const rootClasses = classNames(
    'sui-font-regular sui-text-base sui-leading-tight',
    'sui-relative sui-cursor-text sui-inline-flex',
    'sui-items-center sui-rounded-base',
    'sui-h-11 sui-w-full',
    'sui-text-primary',
    {
      'sui-bg-subtle': !transparent
    }
  );

  const baseInputContainerClasses = [
    'sui-flex',
    'sui-text-current sui-ring-0 sui-rounded-base',
    'sui-outline-none sui-box-border sui-m-0 sui-block',
    'sui-min-w-8 sui-w-full sui-h-11',
    'sui-whitespace-nowrap sui-overflow-hidden',
    'sui-border-1 sui-border-solid sui-border-transparent',
  ].join(' ');

  const inputContainerClasses = classNames(baseInputContainerClasses, customClasses?.inputContainer);

  const inputClasses = [
    'placeholder:sui-text-primary focus:placeholder:sui-text-transparent',
    'sui-min-w-8 sui-w-full sui-h-full sui-text-current sui-ring-0 sui-pl-3',
    'sui-outline-none sui-text-ellipsis sui-appearance-none'
  ].join(' ');

  return (
    <div className={controllerClasses}>
      <form
        className={rootClasses}
        data-testid="search-field-root"
        data-component="typeahead-search-field-root"
        id="header-search"
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...formProps}
      >
        <div
          data-testid="search-field-container"
          className={inputContainerClasses}
        >
          <input
            required
            ref={ref}
            className={inputClasses}
            style={{ font: 'inherit', letterSpacing: 'inherit', background: 'none' }}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...inputProps}
          />
          { ref?.current?.value?.length > 0 && (
            <span
              id={SEARCH_CLEAR_BUTTON_ID}
              className="sui-flex sui-items-center sui-pr-1"
              data-component="typeahead-search-field-clear-icon"
            >
              <IconButton
                data-testid={SEARCH_CLEAR_BUTTON_ID}
                id={SEARCH_CLEAR_BUTTON_ID}
                aria-label="Clear Field Icon"
                icon={Close}
                color="primary"
                iconSize="small"
                onClick={onClearIconClick}
              />
            </span>

          )}
        </div>
        {showSearchIcon && (
          <div className="sui-w-11" data-component="typeahead-search-field-icon">
            <LoadingButton
              type="button"
              fullWidth
              id="typeahead-search-icon-button"
              aria-label="Search Field Icon"
              color="primary"
              variant={transparent ? 'ghost' : 'primary'}
              onClick={onSearchIconClick}
              loading={isLoading}
            >
              <span className="sui-flex sui-items-center">
                <Search size="small" />
              </span>
            </LoadingButton>
          </div>
        )}
      </form>
    </div>
  );
});

SearchField.displayName = 'SearchField';

SearchField.propTypes = {
  onSearchIconClick: func,
  showSearchIcon: bool,
  onClearIconClick: func,
  isLoading: bool,
  inverseTheme: bool,
  transparent: bool,
  formProps: shape(),
  inputProps: shape(),
  customClasses: shape({
    inputContainer: string
  })
};

SearchField.defaultProps = {
  onSearchIconClick: undefined,
  showSearchIcon: true,
  onClearIconClick: undefined,
  isLoading: false,
  inverseTheme: false,
  transparent: false,
  formProps: {},
  inputProps: {},
  customClasses: {
    inputContainer: null
  }
};

export { SearchField };
